import { type Row } from "@tanstack/react-table";
import { type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { DynamicDataTableCell } from "./DynamicDataTableCell";
import { type RowInteraction } from "./types";

type DynamicDataTableRowProps<TData extends object> = RowInteraction<TData> & {
	height: number;
	translateY: number;
	row: Row<TData>;
};

const ROW_CLASS =
	"group absolute top-0 left-0 min-w-full bg-surface-base flex items-center border-b border-bc-subtle text-[15px] text-secondary hover:bg-surface-light hover:text-primary last:border-bc-subtle z-10";

export const dynamicDataTableRowTestId = (rowId: string) => `dynamic-data-table-row-${rowId}`;

export const DynamicDataTableRow = <TData extends object>({
	height,
	translateY,
	row,
	getRowHref,
	onRowClick,
	disableRowClick,
	rowLabel,
}: DynamicDataTableRowProps<TData>) => {
	const style = {
		height: `${height}px`,
		transform: `translateY(${translateY}px)`,
	};

	const cells = row
		.getVisibleCells()
		.map((cell) => <DynamicDataTableCell key={cell.id} cell={cell} />);

	const rowTestId = dynamicDataTableRowTestId(row.id);

	if (getRowHref) {
		return (
			<Link
				to={getRowHref(row.original)}
				className={cn(ROW_CLASS, "cursor-pointer")}
				style={style}
				tabIndex={-1}
				data-testid={rowTestId}
			>
				{cells}
			</Link>
		);
	}

	const handleRowClick =
		onRowClick && !disableRowClick?.(row.original) ? () => onRowClick(row.original) : undefined;

	const keyboardActivation = handleRowClick &&
		rowLabel && {
			role: "button",
			tabIndex: 0,
			"aria-label": rowLabel(row.original),
			onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
				if (event.target !== event.currentTarget) return;
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					handleRowClick();
				}
			},
		};

	return (
		<div
			className={cn(
				ROW_CLASS,
				handleRowClick && "cursor-pointer",
				keyboardActivation &&
					"focus-visible:outline-none focus-visible:bg-surface-light focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-accent",
			)}
			style={style}
			onClick={handleRowClick}
			data-testid={rowTestId}
			{...keyboardActivation}
		>
			{cells}
		</div>
	);
};
